import React from 'react';
import { Challenge } from '../../types';
import { Clock, Target, Zap, Award, Timer, Terminal } from 'lucide-react';

interface ChallengeCardProps {
  challenge: Challenge;
  onClick: () => void;
  userProgress?: {
    completed: boolean;
    attempts: number;
    points_earned: number;
  };
  activeContainer?: {
    container_id: string;
    session_id: string;
    started_at: string;
  } | null;
  onResumeConsole?: (sessionId: string) => void;
}

export const ChallengeCard: React.FC<ChallengeCardProps> = ({ 
  challenge, 
  onClick, 
  userProgress,
  activeContainer,
  onResumeConsole
}) => {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-green-900/50 text-green-200 border-green-500/20';
      case 'Medium':
        return 'bg-yellow-900/50 text-yellow-200 border-yellow-500/20';
      case 'Hard':
        return 'bg-red-900/50 text-red-200 border-red-500/20';
      default:
        return 'bg-gray-900/50 text-gray-200 border-gray-500/20';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return <Target className="w-4 h-4" />;
      case 'Medium':
        return <Clock className="w-4 h-4" />;
      case 'Hard':
        return <Zap className="w-4 h-4" />;
      default:
        return <Target className="w-4 h-4" />;
    }
  };

  const isCompleted = userProgress?.completed || false;

  return (
    <div
      className={`group card p-6 hover:bg-gray-800/70 transition-all duration-300 cursor-pointer transform hover:scale-[1.02] hover:shadow-xl relative ${
        isCompleted ? 'ring-2 ring-green-500/30' : ''
      }`}
      onClick={onClick}
    >
      {/* Completion badge */}
      {isCompleted && (
        <div className="absolute -top-2 -right-2">
          <div className="bg-green-500 rounded-full p-2">
            <Award className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      <div className="flex justify-between items-start mb-4">
        <h3 className="text-xl font-semibold text-white group-hover:text-blue-400 transition-colors">
          {challenge.title}
        </h3>
        <span className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border ${getDifficultyColor(challenge.difficulty)}`}>
          {getDifficultyIcon(challenge.difficulty)}
          <span>{challenge.difficulty}</span>
        </span>
      </div>
      
      <p className="text-gray-400 mb-6 leading-relaxed line-clamp-3">
        {challenge.description}
      </p>

      {/* Challenge stats */}
      <div className="flex items-center justify-between mb-4 text-sm text-gray-400">
        <div className="flex items-center space-x-4">
          {challenge.points && (
            <div className="flex items-center space-x-1">
              <Award className="w-4 h-4" />
              <span>{challenge.points} pts</span>
            </div>
          )}
          {challenge.time_limit_minutes && (
            <div className="flex items-center space-x-1">
              <Timer className="w-4 h-4" />
              <span>{challenge.time_limit_minutes}m</span>
            </div>
          )}
        </div>
        
        {userProgress && userProgress.attempts > 0 && (
          <div className="text-xs">
            Attempts: {userProgress.attempts}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="space-y-2">
        {activeContainer && onResumeConsole ? (
          <button
            className="w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 transform group-hover:scale-105 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center space-x-2"
            onClick={(e) => {
              e.stopPropagation();
              onResumeConsole(activeContainer.session_id);
            }}
          >
            <Terminal className="w-4 h-4" />
            <span>Resume Console</span>
          </button>
        ) : (
          <button
            className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 transform group-hover:scale-105 btn-primary`}
            onClick={e => {
              e.stopPropagation();
              onClick();
            }}
          >
            Check Challenge
          </button>
        )}
      </div>
    </div>
  );
};
